import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import api from "api/api";
import Select from "react-select";

// mui components
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";

// mui icons
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

// redux
import { useDispatch, useSelector } from "react-redux";
import { showNotification } from "store/features/notificationSlice";

// components
import Loader from "../../common/Loader";

// prop validation
import PropTypes from "prop-types";
import Creatable from "react-select/creatable";

// utils
import FlipScaleVariablesList from "../../../utils/FlipScaleVariablesList";

const LightTooltip = styled(({ className, offset, ...props }) => (
    <Tooltip
        {...props}
        classes={{ popper: className }}
        PopperProps={{
            modifiers: [
                {
                    name: "offset",
                    options: {
                        offset,
                    },
                },
            ],
        }}
    />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.common.white,
        color: "rgba(0, 0, 0, 0.87)",
        boxShadow: theme.shadows[1],
        fontSize: "0.6rem",
        width: "280px",
        border: "1px solid rgb(51, 94, 234, 0.5)",
        padding: "2px",
    },
}));

function AddUpdateComponent({ title = "", type = "", selectedRow = null, fetchData = () => { }, dataView = {}, isOpen = false, setIsAddDialogOpen = () => { }, setIsUpdateDialogOpen = () => { }, selectedStudyType = null, selectedStudyTypeMethod = null }) {
    const dispatch = useDispatch();
    const selectInputRef = useRef();
    const [isLoading, setIsLoading] = useState(false);
    const [selectedDataType, setSelectedDataType] = useState(null);
    const [selectedTypeOfAnalysis, setSelectedTypeOfAnalysis] = useState(null);
    const [studyReferences, setStudyReferences] = useState([]);
    const [cautionFlag, setCautionFlag] = useState({});
    const [backendVariableSearchText, setBackendVariableSearchText] = useState("");
    const [numericalVariables, setNumericalVariables] = useState([]);
    const [data, setData] = useState({
        backend_variable: "",
        macro_category: "",
        variable_category: "",
        display_label: "",
        attribute: "",
        attribute_value: "",
        data_type: "",
        type_of_analysis: "",
    });
    const [tooltipContent, setTooltipContent] = useState({
        backend_variable: { flag: false, message: "" },
        macro_category: { flag: false, message: "" },
        variable_category: { flag: false, message: "" },
        display_label: { flag: false, message: "" },
        attribute: { flag: false, message: "" },
        attribute_value: { flag: false, message: "" },
        data_type: { flag: false, message: "" },
        type_of_analysis: { flag: false, message: "" },
    });
    const [errorTooltipContent, setErrorTooltipContent] = useState({
        backend_variable: { flag: false, message: "" },
        macro_category: { flag: false, message: "" },
        variable_category: { flag: false, message: "" },
        display_label: { flag: false, message: "" },
        attribute: { flag: false, message: "" },
        attribute_value: { flag: false, message: "" },
        data_type: { flag: false, message: "" },
        type_of_analysis: { flag: false, message: "" },
    });

    const [selectedVariableCategory, setSelectedVariableCategory] = useState(null);
    const [selectedMacroCategory, setSelectedMacroCategory] = useState(null);
    const [variableCategoryOptions, setVariableCategoryOptions] = useState([]);
    const [macroCategoryOptions, setMacroCategoryOptions] = useState([]);
    const [fieldsData, setFieldsData] = useState({});

    const productCategoryId = useSelector((state) => state.settingsStore.productCategory?.value);
    const productCategory = useSelector((state) => state.settingsStore.productCategory);
    const productCategoryLabel = productCategory?.label || productCategory?.name || productCategory?.value || "";

    const studyTypeMethods = useMemo(
        () => [...new Set(selectedStudyTypeMethod?.map((datum) => datum.value))],
        [selectedStudyTypeMethod]
    );
    const dataTypeOptions = useMemo(
        () => [
            { value: 0, label: "Categorical" },
            { value: 1, label: "Numerical" },
            { value: 2, label: "Alphanumerical/ Categorical" },
            { value: 3, label: "Open Ended" },
        ],
        []
    );

    const typeOfAnalysisOptions = useMemo(
        () => [
            { value: "Theme Text Analysis + Sentiment Bifurcation", label: "Theme Text Analysis + Sentiment Bifurcation" },
            { value: "Theme Text Analysis", label: "Theme Text Analysis" },
        ],
        []
    );

    const showTypeOfAnalysis = productCategoryLabel === "Deodorants" && selectedStudyType.label === "Home Use Test" && data.variable_category === "Open Ended" && data.data_type === "Open Ended";


    const isOpenEndedVariable = data.variable_category === "Open Ended" && data.data_type === "Open Ended";
    const numericalBackendVariableOptions = useMemo(() => {
        return numericalVariables.map((variable) => ({
            value: variable,
            label: variable,
        }));
    }, [numericalVariables]);
    const macroCategoryDisplayOptions = useMemo(() => {
        if (data.variable_category === "Open Ended") { 
            return numericalBackendVariableOptions;
        }
        return macroCategoryOptions;
    }, [data.variable_category, numericalBackendVariableOptions, macroCategoryOptions]);
    const getMacroCategorySourceOptions = useCallback(
        (variableCategory) => {
            return variableCategory === "Open Ended" ? numericalBackendVariableOptions : macroCategoryOptions;
        },
        [numericalBackendVariableOptions, macroCategoryOptions]
    );

    const handleError = useCallback(
        (type, error) => {
            dispatch(showNotification({ severity: type, data: error }));
        },
        [dispatch]
    );
    const fetchOpenEndedVariables = useCallback(
        async () => {
            if (!productCategoryId || !selectedStudyType?.value || !studyTypeMethods?.length) return;
            setIsLoading(true);
            try {
                const url =
                    process.env.REACT_APP_SERVER_PATH + `/data-mappings/field-suggestions/?product_category=${productCategoryId}` + `&study_type=${selectedStudyType.value}` + `&study_type_method=${studyTypeMethods.join(",")}` + `&type=OpenEnded`;
                const res = await api.get(url);

                setNumericalVariables(res.data.open_ended_marco_category || []);
                setIsLoading(false);
            } catch (error) {
                handleError("error", error);
                setIsLoading(false);
            }
        },
        [
            productCategoryId,
            selectedStudyType?.value,
            studyTypeMethods,
            handleError,
        ]
    );

    const handleSearch = useCallback(async () => {
        if (productCategoryId && selectedStudyType?.value && studyTypeMethods?.length > 0 && backendVariableSearchText.trim() !== "") {
            let url = process.env.REACT_APP_SERVER_PATH + `/data-mappings/fields/?product_category=${productCategoryId}&study_type=${selectedStudyType.value}&study_type_method=${studyTypeMethods.join(",")}&backend_variable=${backendVariableSearchText}`;
            try {
                await api
                    .get(url)
                    .then(async (res) => {
                        const result = res.data;
                        let existingAttributeValue = "";
                        const isOpenEndedVariable = result?.variable_category === "Open Ended" && result?.data_type === "Open Ended";
                        if (isOpenEndedVariable) {
                            try {
                                const existingUrl = process.env.REACT_APP_SERVER_PATH + `/data-mappings/existing-fields/?product_category=${productCategoryId}` + `&study_type=${selectedStudyType.value}` +
                                    `&study_type_method=${studyTypeMethods.join(",")}` +
                                    `&backend_variable=${backendVariableSearchText}`;
                                const existingRes = await api.get(existingUrl);
                                const attributeValueArray = existingRes.data?.attribute_value ?? [];
                                const validValues = Array.isArray(attributeValueArray) ? attributeValueArray.filter((val) =>
                                            val !== "" &&
                                            val !== null &&
                                            val !== undefined ) : [];
                                existingAttributeValue = validValues.length > 0 ? validValues[0] : "";
                            } catch (err) {
                                handleError("error", err);
                            }
                        }
                        setData((prev) => ({
                            ...prev,
                            ...result,
                            attribute_value: isOpenEndedVariable ? existingAttributeValue : "",
                        }));
                        const dataType = dataTypeOptions.filter((datum) => datum.label === result?.data_type);
                        const isOpenEnded = result?.variable_category === "Open Ended";
                        const macroSourceOptions = isOpenEnded ? numericalBackendVariableOptions : macroCategoryOptions;
                        const macroCat = macroSourceOptions.filter((datum) => datum.label === result?.macro_category);
                        const varCat = variableCategoryOptions.filter((datum) => datum.label === result?.variable_category);
                        const typeOfAn = typeOfAnalysisOptions.filter((datum) => datum.label === result?.type_of_analysis);
                        setSelectedDataType(dataType);
                        setSelectedMacroCategory(macroCat.length ? macroCat : null);
                        setSelectedVariableCategory(varCat);
                        setSelectedTypeOfAnalysis(typeOfAn.length ? typeOfAn : null);
                        const flagObj = Object.fromEntries(Object.entries(result).map(([key, val]) => [key, val && val != null && val !== ""]));
                        setCautionFlag({
                        ...flagObj,
                        attribute_value: isOpenEndedVariable
                            ? existingAttributeValue !== ""
                            : false,
                        });
                    })
                    .catch(async (error) => {
                        handleError("error", error);
                    });
            } catch (error) {
                handleError("error", error);
            }
        }
    }, [productCategoryId, handleError, backendVariableSearchText, selectedStudyType?.value, selectedStudyTypeMethod?.value, dataTypeOptions, macroCategoryOptions, variableCategoryOptions, typeOfAnalysisOptions, ]);

    useEffect(() => {
        if (type !== "add") return;

        const debounce = setTimeout(() => {
            handleSearch();
        }, 500);

        return () => clearInterval(debounce);
    }, [type, handleSearch]);

    const createOptions = (data) => {
        return data.map((datum) => ({ value: datum, label: datum }));
    };

    const fetchVariableCategoryAndMacroCategoryOptions = useCallback(async () => {
        try {
            const url = process.env.REACT_APP_SERVER_PATH + `/data-mappings/field-suggestions/?product_category=${productCategoryId}&study_type=${selectedStudyType.value}&study_type_method=${studyTypeMethods.join(",")}`;
            await api
                .get(url)
                .then((res) => {
                    const variableCatOptions = createOptions(res.data.variable_category);
                    const macroCatOptions = createOptions(res.data.macro_category);
                    setVariableCategoryOptions(variableCatOptions);
                    setMacroCategoryOptions(macroCatOptions);
                    if (type === "update" && selectedRow) {
                        const variableCat = variableCatOptions.filter((datum) => datum.label === selectedRow?.variable_category);
                        const isOpenEnded = selectedRow?.variable_category === "Open Ended";
                        if (!isOpenEnded) {
                            const macroCat = macroCatOptions.filter((datum) => datum.label === selectedRow?.macro_category);
                            setSelectedMacroCategory(macroCat.length ? macroCat : null);
                        }
                        setSelectedVariableCategory(variableCat);
                    }
                })
                .catch((error) => {
                    handleError(error);
                });
        } catch (error) {
            handleError(error);
        }
    }, [productCategoryId, selectedStudyType, selectedStudyTypeMethod, handleError, type, selectedRow,]);

    useEffect(() => {
        fetchVariableCategoryAndMacroCategoryOptions();
    }, [fetchVariableCategoryAndMacroCategoryOptions]);

    useEffect(() => {
        if (data.variable_category === "Open Ended") {
            fetchOpenEndedVariables();
        }
    }, [data.variable_category, fetchOpenEndedVariables]);

    useEffect(() => {
        if (type === "update" && selectedRow && numericalVariables.length > 0) {
            const isOpenEnded = selectedRow?.variable_category === "Open Ended";
            if (!isOpenEnded) return;

            const macroCat = numericalBackendVariableOptions.filter((datum) => datum.label === selectedRow?.macro_category);
            setSelectedMacroCategory(macroCat.length ? macroCat : null);
        }
    }, [numericalVariables, type, selectedRow, numericalBackendVariableOptions]);

    const fetchFieldsData = useCallback(async () => {
        if (!data.backend_variable) {
            return;
        }
        try {
            let url = process.env.REACT_APP_SERVER_PATH + `/data-mappings/existing-fields/?product_category=${productCategoryId}&study_type=${selectedStudyType.value}&study_type_method=${studyTypeMethods.join(",")}`;
            if (data.backend_variable) {
                url += `&backend_variable=${data.backend_variable}`;
            }
            await api
                .get(url)
                .then((res) => {
                    setFieldsData(res.data);
                })
                .catch((error) => {
                    handleError(error);
                });
        } catch (error) {
            handleError(error);
        }
    }, [data.backend_variable, productCategoryId, selectedStudyType, selectedStudyTypeMethod, handleError]);

    useEffect(() => {
        fetchFieldsData();
    }, [fetchFieldsData]);

    useEffect(() => {
        if (type === "update" && selectedRow) {
            const dataType = dataTypeOptions.filter((datum) => datum.label === selectedRow?.data_type);
            const typeOfAnUpdate = typeOfAnalysisOptions.filter((datum) => datum.label === selectedRow?.type_of_analysis);
            setSelectedDataType(dataType);
            setSelectedTypeOfAnalysis(typeOfAnUpdate.length ? typeOfAnUpdate : null);
            setData({
                backend_variable: selectedRow?.backend_variable || "",
                macro_category: selectedRow?.macro_category || "",
                variable_category: selectedRow?.variable_category || "",
                display_label: selectedRow?.display_label || "",
                attribute: selectedRow?.attribute || "",
                attribute_value: selectedRow?.attribute_value || "",
                data_type: selectedRow?.data_type || "",
                type_of_analysis: selectedRow?.type_of_analysis || "",
            });
            const isOpenEndedRow = selectedRow?.variable_category === "Open Ended" && selectedRow?.data_type === "Open Ended";
            setCautionFlag((prev) => ({
                ...prev,
                attribute_value: isOpenEndedRow ? (selectedRow?.attribute_value !== "" && selectedRow?.attribute_value != null) : false,
            }));
        }
    }, [selectedRow, type, dataTypeOptions, typeOfAnalysisOptions]);

    useEffect(() => {
        const variableCategoryList = ["Consumer Filter", "Consumer Filter (ITM)", "Consumer Filter related to Product Trial", "Additional Product Performance", "Study Filter"];
        if (type === "update" && dataView.value === 1 && selectedRow?.macro_category === "" && variableCategoryList.includes(selectedRow?.variable_category)) {
            setTooltipContent((prev) => ({
                ...prev,
                macro_category: {
                    flag: true,
                    message: (
                        <>
                            <p>{`There is no 'Variable Product Performance Macro-Category' assigned to the backend variable "${data.backend_variable}" and it's visibility on the tool is driven solely by its 'variable category'.`}</p>
                            <p>Please note that the updated new macro-category will not appear anywhere in the tool. </p>
                        </>
                    ),
                },
            }));
        }
        if (type === "update" && dataView.value === 1 && selectedRow?.data_type === "Numerical") {
            setTooltipContent((prev) => ({
                ...prev,
                attribute: {
                    flag: true,
                    message: "Attributes of backend variables with a numerical data type cannot be updated",
                },
            }));
            setCautionFlag((prev) => ({
                ...prev,
                data_type: true,
            }));
        }
    }, [type, selectedRow, data, dataView.value]);

    const getStudyReferences = useCallback(async () => {
        if (productCategoryId && selectedStudyType?.value && studyTypeMethods.length > 0) {
            let url = process.env.REACT_APP_SERVER_PATH + `/data-mappings/study-references/?product_category=${productCategoryId}&study_type=${selectedStudyType.value}&study_type_method=${studyTypeMethods.join(",")}&backend_variable=${selectedRow?.backend_variable}`;
            try {
                await api
                    .get(url)
                    .then(async (res) => {
                        setStudyReferences(res.data);
                    })
                    .catch(async (error) => {
                        handleError("error", error);
                    });
            } catch (error) {
                handleError("error", error);
            }
        }
    }, [productCategoryId, handleError, selectedStudyType?.value, selectedStudyTypeMethod?.value, selectedRow?.backend_variable]);

    useEffect(() => {
        if (type === "update" && selectedRow) {
            getStudyReferences();
        }
    }, [type, selectedRow, getStudyReferences]);

    const handleReset = () => {
        setCautionFlag({});
        setBackendVariableSearchText("");
        selectInputRef.current.clearValue();
        if (type === "add") {
            setData({
                backend_variable: "",
                macro_category: "",
                variable_category: "",
                display_label: "",
                attribute: "",
                attribute_value: "",
                data_type: "",
                type_of_analysis: "",
            });
            setSelectedDataType(null);
            setSelectedMacroCategory(null);
            setSelectedVariableCategory(null);
            setSelectedTypeOfAnalysis(null);
        } else if (type === "update") {
            const dataType = dataTypeOptions.filter((datum) => datum.label === selectedRow?.data_type);
            const sourceOptions = getMacroCategorySourceOptions(selectedRow?.variable_category);
            const macroCat = sourceOptions.filter((datum) => datum.label === selectedRow?.macro_category);
            const varCat = variableCategoryOptions.filter((datum) => datum.label === selectedRow?.variable_category);
            const typeOfAn = typeOfAnalysisOptions.filter((datum) => datum.label === selectedRow?.type_of_analysis);
            setSelectedDataType(dataType);
            setSelectedMacroCategory(macroCat.length ? macroCat : null);
            setSelectedVariableCategory(varCat);
            setSelectedTypeOfAnalysis(typeOfAn.length ? typeOfAn : null);
            setData({
                backend_variable: selectedRow?.backend_variable || "",
                macro_category: selectedRow?.macro_category || "",
                variable_category: selectedRow?.variable_category || "",
                display_label: selectedRow?.display_label || "",
                attribute: selectedRow?.attribute || "",
                attribute_value: selectedRow?.attribute_value || "",
                data_type: selectedRow?.data_type || "",
                type_of_analysis: selectedRow?.type_of_analysis || "",
            });
        }
        setTooltipContent({
            backend_variable: { flag: false, message: "" },
            macro_category: { flag: false, message: "" },
            variable_category: { flag: false, message: "" },
            display_label: { flag: false, message: "" },
            attribute: { flag: false, message: "" },
            attribute_value: { flag: false, message: "" },
            data_type: { flag: false, message: "" },
            type_of_analysis: { flag: false, message: "" },
        });
        setErrorTooltipContent({
            backend_variable: { flag: false, message: "" },
            macro_category: { flag: false, message: "" },
            variable_category: { flag: false, message: "" },
            display_label: { flag: false, message: "" },
            attribute: { flag: false, message: "" },
            attribute_value: { flag: false, message: "" },
            data_type: { flag: false, message: "" },
            type_of_analysis: { flag: false, message: "" },
        });
    };

    const handleClose = () => {
        handleReset();
        type === "add" ? setIsAddDialogOpen(false) : setIsUpdateDialogOpen(false);
    };

    const handleTooltipContent = (value, field) => {
        if (type !== "update") return;

        const updateObj = {};
        if (dataView.value === 1 && field === "backend_variable" && selectedRow?.backend_variable !== value) {
            updateObj.backend_variable = {
                flag: true,
                message: (
                    <div>
                        {FlipScaleVariablesList.includes(selectedRow?.backend_variable) && (
                            <div style={{ marginBottom: "16px" }}>
                                <span style={{ fontWeight: "600" }}>This is a &apos;Flip-Scale&apos; variable.</span>
                                <span>Modifying its name will cause it to lose &apos;Flip-Scale&apos; recognition, as the Flip-Scale variable list is predefined and cannot be dynamically updated</span>
                            </div>
                        )}
                        <p>The backend variable name you are attempting to update is currently used in the following studies. Updating it will affect unsaved Driver Analysis reports and Action Standards results.</p>
                        <p>Please note: You will not be able to re-calculate the impacted Driver Analysis reports, and will need to reconfigure the Action Standard criteria for each affected study</p>
                        <ul>
                            {studyReferences.map((datum) => (
                                <li key={datum}>{datum}</li>
                            ))}
                        </ul>
                    </div>
                ),
            };
        } else if (dataView.value === 1 && field === "backend_variable") {
            updateObj.backend_variable = {
                flag: false,
                message: "",
            };
        }

        if (dataView.value === 1 && field === "macro_category" && selectedRow?.macro_category !== "" && selectedRow?.macro_category !== value) {
            const isOpenEnded = data.data_type?.toLowerCase().includes("open") || data.variable_category?.toLowerCase().includes("open");

            if (isOpenEnded) {
                updateObj.macro_category = {
                    flag: true,
                    message: (
                        <>
                            <p style={{ marginBottom: "8px" }}>
                                {`You are about to change the backend variable "${selectedRow?.macro_category}" related to open ended backend variable "${data.backend_variable}".`}
                            </p>
                            <p style={{ marginBottom: 0 }}>
                                {`Please note going forward the "${data.backend_variable}" will be related to "${value}"`}
                            </p>
                        </>
                    ),
                };
            } else {
                updateObj.macro_category = {
                    flag: true,
                    message: (
                        <>
                            <p>{`You are about to modify the variable "${data.backend_variable}" currently assigned to the "${selectedRow?.macro_category}" macro-performance category. You are attempting to move this variable to a "${value}" macro-performance category.`}</p>
                            <p>{`Please note that in any existing studies, this variable will now appear under the new macro-performance category: "${value}"`}</p>
                        </>
                    ),
                };
            }
        } else if (dataView.value === 1 && field === "macro_category") {
            updateObj.macro_category = {
                flag: false,
                message: "",
            };
        }

        if (dataView.value === 1 && field === "variable_category" && selectedRow?.variable_category !== value) {
            updateObj.variable_category = {
                flag: true,
                message: (
                    <>
                        <p>{`You are about to move an existing Product Performance Variable "${data.backend_variable}" from the category "${selectedRow?.variable_category}" to "${value}".This change may be significant and could impact how the variable is interpreted across studies.`}</p>
                        <p>{`Please note: in any existing studies, this variable will now appear under the new category: "${value}"`}</p>
                    </>
                ),
            };
        } else if (dataView.value === 1 && field === "variable_category") {
            updateObj.variable_category = {
                flag: false,
                message: "",
            };
        }
        setTooltipContent((prev) => ({ ...prev, ...updateObj }));
    };

    const handleCreateNewOptions = (val, field) => {
        const option = { value: val, label: val };
        if (field === "variable_category") {
            setData((prevState) => ({ ...prevState, [field]: val }));
            setSelectedVariableCategory(option);
            setVariableCategoryOptions([option, ...variableCategoryOptions]);
        } else if (field === "macro_category") {
            setData((prevState) => ({ ...prevState, [field]: val }));
            setSelectedMacroCategory(option);
            setMacroCategoryOptions([option, ...macroCategoryOptions]);
        }
    };

    const updateFieldErrors = (field, selectedValue) => {
        let updatedObj = {};
        let dataArray = fieldsData[field];
        if (type === "update" && selectedValue) {
            dataArray = fieldsData[field].filter((datum) => datum !== selectedRow[field]);
        }

        if (field === "display_label") {
            if (dataArray?.includes(selectedValue)) {
                updatedObj.display_label = {
                    flag: true,
                    message: "Display label must be unique",
                };
            } else {
                updatedObj.display_label = {
                    flag: false,
                    message: "",
                };
            }
        } else if (field === "attribute") {
            if (dataArray?.includes(selectedValue)) {
                updatedObj.attribute = {
                    flag: true,
                    message: "Duplicate attributes cannot be updated.",
                };
            } else {
                updatedObj.attribute = {
                    flag: false,
                    message: "",
                };
            }
        } else if (field === "attribute_value") {
            if (dataArray?.includes(selectedValue)) {
                updatedObj.attribute_value = {
                    flag: true,
                    message: "Duplicate attribute values cannot be updated.",
                };
            } else {
                updatedObj.attribute_value = {
                    flag: false,
                    message: "",
                };
            }
        }
        setErrorTooltipContent((prev) => ({ ...prev, ...updatedObj }));
    };

    const handleInputChange = (selectedValue, type, field) => {
        if (type === "dropdown") {
            if (selectedValue && Object.keys(selectedValue).length !== 0) {
                handleTooltipContent(selectedValue.label, field);
                let updatedData = { ...data, [field]: selectedValue.label };
                const newVariableCategory = field === "variable_category" ? selectedValue.label : data.variable_category;
                const newDataType = field === "data_type" ? selectedValue.label : data.data_type;
                if (newVariableCategory === "Open Ended" && newDataType === "Open Ended") {
                    updatedData.attribute = "";
                }
                if (field === "variable_category") {
                    updatedData.macro_category = "";
                    setSelectedMacroCategory(null);
                }
                setData(updatedData);
            } else {
                if (field === "variable_category") {
                    setData({ ...data, [field]: "", macro_category: "" });
                    setSelectedMacroCategory(null);
                    return;
                }
                setData({
                    ...data,
                    [field]: "",
                });
            }
        } else if (type === "text") {
            if (selectedValue && selectedValue !== "") {
                handleTooltipContent(selectedValue, field);
                setData({
                    ...data,
                    [field]: selectedValue,
                });
            } else {
                setData({
                    ...data,
                    [field]: "",
                });
            }
        }
        if (field === "display_label" || field === "attribute" || field === "attribute_value") {
            updateFieldErrors(field, selectedValue);
        }
    };

    const validateInputs = () => {
        if (!productCategoryId) {
            handleError("error", "Product Category is required");
            return false;
        }

        if (!selectedStudyType?.value) {
            handleError("error", "Study Type is required");
            return false;
        }

        if (!studyTypeMethods?.length > 0) {
            handleError("error", "Study Type Method is required");
            return false;
        }
        if (showTypeOfAnalysis && (!data.type_of_analysis || data.type_of_analysis === "")) {
            handleError("error", "Type of Analysis is required");
            return false;
        }

        const isEmpty = Object.keys(data).some((key) => {
            if (key === "macro_category" || key === "variable_category" || key === "display_label" || key === "data_type" || key === "attribute_value" || key === "attribute" || key === "type_of_analysis") {
                return false;
            }

            return data[key] === "";
        });

        if (isEmpty) {
            handleError("error", "Please fill all the fields");
            return false;
        }

        return true;
    };

    const updateErrors = (error) => {
        const status = error?.response?.status;
        if (status === 400) {
            setIsLoading(false);
            const errors = error?.response?.data;
            const updated = Object.fromEntries(Object.entries(errors).map(([k, v]) => [k, { flag: true, message: v }]));
            setErrorTooltipContent((prev) => ({
                ...prev,
                ...updated,
            }));
            handleError("error", error);
        } else {
            handleError("error", error);
            setIsLoading(false);
        }
    };

    const handleSubmit = async () => {
        const validate = validateInputs();
        if (!validate) return;

        const payload = {
            ...data,
            product_category: productCategoryId,
            study_type: selectedStudyType?.value,
            study_type_method: studyTypeMethods,
        };

        if (type === "add") {
            setIsLoading(true);
            try {
                const url = process.env.REACT_APP_SERVER_PATH + `/data-mappings/`;
                api.post(url, payload)
                    .then(() => {
                        fetchData();
                        setIsLoading(false);
                        handleClose();
                    })
                    .catch((error) => {
                        updateErrors(error);
                    });
            } catch (error) {
                handleError("error", error);
                setIsLoading(false);
            }
        } else if (type === "update") {
            try {
                setIsLoading(true);
                const url = process.env.REACT_APP_SERVER_PATH + `/data-mappings/${selectedRow?.id}/`;
                api.patch(url, payload)
                    .then(() => {
                        fetchData();
                        setIsLoading(false);
                        handleClose();
                    })
                    .catch((error) => {
                        updateErrors(error);
                    });
            } catch (error) {
                handleError("error", error);
                setIsLoading(false);
            }
        }
    };

    return (
        <>
            {isLoading && <Loader />}
            <Dialog
                open={isOpen}
                // onClose={handleClose}
                fullWidth={true}
                maxWidth="md"
                aria-labelledby="customized-dialog-title"
                style={{
                    zIndex: 1300,
                }}
            >
                <DialogTitle
                    id="customized-dialog-title"
                    style={{
                        backgroundColor: "rgb(51, 94, 234,0.1)",
                        color: "rgb(51, 94, 234)",
                        fontSize: 15,
                        textAlign: "center",
                        padding: "10px 12px",
                    }}
                >
                    {title}
                </DialogTitle>
                <IconButton
                    aria-label="close dialog"
                    onClick={handleClose}
                    sx={{
                        position: "absolute",
                        right: 8,
                        top: 2,
                        color: "rgb(51, 94, 234) !important",
                    }}
                >
                    <CloseIcon />
                </IconButton>
                <DialogContent>
                    <div className="row pb-2">
                        <div className="col-4">
                            <label
                                htmlFor="backendVariable"
                                className="input-label vertical-center text-sm"
                            >
                                Backend Variable:
                                {(tooltipContent.backend_variable.flag || errorTooltipContent.backend_variable.flag) && (
                                    <LightTooltip
                                        title={tooltipContent.backend_variable.message || errorTooltipContent.backend_variable.message}
                                        offset={[0, -12]}
                                        placement="bottom"
                                    >
                                        <WarningAmberIcon
                                            style={{
                                                fontWeight: "600",
                                                color: "#FF0000",
                                                fontSize: "16px",
                                                marginLeft: "3px",
                                            }}
                                        />
                                    </LightTooltip>
                                )}
                            </label>
                        </div>
                        <div className="col-8">
                            <input
                                id="backendVariable"
                                type="text"
                                value={data.backend_variable}
                                className="input-label-field text-sm"
                                onChange={(e) => {
                                    handleInputChange(e.target.value, "text", "backend_variable");
                                    setBackendVariableSearchText(e.target.value);
                                }}
                                aria-required="true"
                                autoComplete="off"
                            />
                        </div>
                    </div>

                    <div className="row pb-2">
                        <div className="col-4">
                            <div className="d-flex flex-column">
                                <label
                                    htmlFor="macroCategory"
                                    className="input-label text-sm pe-1 d-flex align-items-center mb-0"
                                >
                                    Variable Product Performance Macro-Category:
                                    {(tooltipContent.macro_category.flag || errorTooltipContent.macro_category.flag) && (
                                        <LightTooltip
                                            title={tooltipContent.macro_category.message || errorTooltipContent.macro_category.message}
                                            offset={[56, -12]}
                                            placement="bottom"
                                        >
                                            <WarningAmberIcon
                                                style={{
                                                    fontWeight: "600",
                                                    color: "#FF0000",
                                                    fontSize: "16px",
                                                    marginLeft: "3px",
                                                }}
                                            />
                                        </LightTooltip>
                                    )}
                                </label>
                                {(productCategoryLabel === "Deodorants" && selectedStudyType.label === "Home Use Test") && (
                                    <span style={{ fontSize: "10px", color: "#6c757d", marginTop: "2px", lineHeight: "1.2" }}>
                                        (In case of open ended question, select the metric linked to the open-ended question)
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="col-8">
                            {data.variable_category === "Open Ended" ? (
                                <Select
                                    id="macroCategory"
                                    styles={{
                                        menuPortal: (base) => ({ ...base, zIndex: 9999, fontSize: "0.875rem", lineHeight: "1.25rem" }),
                                        menu: (base) => ({ ...base, zIndex: 9999 }),
                                        menuList: (base) => ({ ...base, maxHeight: "200px" }),
                                    }}
                                    menuPortalTarget={document.body}
                                    className="text-sm input-label-select"
                                    closeMenuOnSelect={true}
                                    isClearable={true}
                                    isSearchable={true}
                                    isMulti={false}
                                    options={macroCategoryDisplayOptions}
                                    value={selectedMacroCategory}
                                    onChange={(selected) => {
                                        handleInputChange(selected, "dropdown", "macro_category");
                                        setSelectedMacroCategory(selected);
                                    }}
                                />
                            ) : (
                                <Creatable
                                    id="macroCategory"
                                    styles={{
                                        menuPortal: (base) => ({
                                            ...base,
                                            zIndex: 9999,
                                            fontSize: "0.875rem",
                                            lineHeight: "1.25rem",
                                        }),
                                        menu: (base) => ({
                                            ...base,
                                            zIndex: 9999,
                                        }),
                                        menuList: (base) => ({
                                            ...base,
                                            maxHeight: "200px",
                                        }),
                                    }}
                                    menuPortalTarget={document.body}
                                    className="text-sm input-label-select"
                                    closeMenuOnSelect={true}
                                    isClearable={true}
                                    isSearchable={true}
                                    isMulti={false}
                                    options={macroCategoryOptions}
                                    onCreateOption={(newValue) => handleCreateNewOptions(newValue, "macro_category")}
                                    value={selectedMacroCategory}
                                    onChange={(selected) => {
                                        handleInputChange(selected, "dropdown", "macro_category");
                                        setSelectedMacroCategory(selected);
                                    }}
                                    isDisabled={type === 'add' ? cautionFlag.macro_category : false}
                                />
                            )}
                        </div>
                    </div>

                    <div className="row pb-2">
                        <div className="col-4">
                            <label
                                htmlFor="variableCategory"
                                className="input-label vertical-center text-sm"
                            >
                                Variable Category:
                                {(tooltipContent.variable_category.flag || errorTooltipContent.variable_category.flag) && (
                                    <LightTooltip
                                        title={tooltipContent.variable_category.message || errorTooltipContent.variable_category.message}
                                        offset={[-6, -12]}
                                        placement="bottom"
                                    >
                                        <WarningAmberIcon
                                            style={{
                                                fontWeight: "600",
                                                color: "#FF0000",
                                                fontSize: "16px",
                                                marginLeft: "3px",
                                            }}
                                        />
                                    </LightTooltip>
                                )}
                            </label>
                        </div>
                        <div className="col-8">
                            <Creatable
                                id="variableCategory"
                                styles={{
                                    menuPortal: (base) => ({
                                        ...base,
                                        zIndex: 9999,
                                        fontSize: "0.875rem",
                                        lineHeight: "1.25rem",
                                    }),
                                    menu: (base) => ({
                                        ...base,
                                        zIndex: 9999,
                                    }),
                                    menuList: (base) => ({
                                        ...base,
                                        maxHeight: "200px",
                                    }),
                                }}
                                menuPortalTarget={document.body}
                                className="text-sm input-label-select"
                                closeMenuOnSelect={true}
                                isClearable={true}
                                isSearchable={true}
                                isMulti={false}
                                options={variableCategoryOptions}
                                onCreateOption={(newValue) => handleCreateNewOptions(newValue, "variable_category")}
                                value={selectedVariableCategory}
                                onChange={(selected) => {
                                    handleInputChange(selected, "dropdown", "variable_category");
                                    setSelectedVariableCategory(selected);
                                }}
                                isDisabled={type === 'add' ? cautionFlag.variable_category : false}
                            />
                        </div>
                    </div>

                    <div className="row pb-2 align-items-center">
                        <div className="col-4">
                            <div className="d-flex flex-column">
                                <label
                                    htmlFor="displayLabel"
                                    className="input-label text-sm"
                                >
                                    Display Label:
                                    {(tooltipContent.display_label.flag || errorTooltipContent.display_label.flag) && (
                                        <LightTooltip
                                            title={tooltipContent.display_label.message || errorTooltipContent.display_label.message}
                                            offset={[32, -12]}
                                            placement="bottom"
                                        >
                                            <ErrorOutlineIcon
                                                style={{
                                                    fontWeight: "600",
                                                    color: "#FF0000",
                                                    fontSize: "16px",
                                                    marginLeft: "3px",
                                                }}
                                            />
                                        </LightTooltip>
                                    )}
                                </label>
                            </div>
                        </div>
                        <div className="col-8">
                            <input
                                id="displayLabel"
                                type="text"
                                value={data.display_label}
                                className="input-label-field text-sm"
                                style={{
                                    color: (type === 'add' && cautionFlag.display_label) ? "rgb(153, 153, 153)" : "rgb(48, 48, 48)",
                                }}
                                onChange={(e) => handleInputChange(e.target.value, "text", "display_label")}
                                aria-required="true"
                                disabled={type === 'add' ? cautionFlag.display_label : false}
                                autoComplete="off"
                            />
                        </div>
                    </div>

                    <div className="row pb-2 align-items-center">
                        <div className="col-4">
                            <div className="d-flex flex-column">
                                <label
                                    htmlFor="attribute"
                                    className="input-label text-sm"
                                >
                                    Attribute:
                                    {(tooltipContent.attribute.flag || errorTooltipContent.attribute.flag) && (
                                        <LightTooltip
                                            title={tooltipContent.attribute.message || errorTooltipContent.attribute.message}
                                            offset={[64, -12]}
                                            placement="bottom"
                                        >
                                            <ErrorOutlineIcon
                                                style={{
                                                    fontWeight: "600",
                                                    color: "#FF0000",
                                                    fontSize: "16px",
                                                    marginLeft: "3px",
                                                }}
                                            />
                                        </LightTooltip>
                                    )}
                                </label>
                            </div>
                        </div>
                        <div className="col-8">
                            <input
                                id="attribute"
                                type="text"
                                value={data.attribute}
                                className="input-label-field text-sm"
                                style={{
                                    color: cautionFlag.attribute || (type === "update" && dataView.value === 1 && data.data_type === "Numerical") || isOpenEndedVariable ? "rgb(153, 153, 153)" : "rgb(48, 48, 48)",
                                    backgroundColor: isOpenEndedVariable ? "rgb(242, 242, 242)" : "inherit",
                                }}
                                onChange={(e) => handleInputChange(e.target.value, "text", "attribute")}
                                disabled={cautionFlag.attribute || (type === "update" && dataView.value === 1 && data.data_type === "Numerical") || isOpenEndedVariable}
                                autoComplete="off"
                            />
                        </div>
                    </div>

                    <div className="row pb-2 align-items-center">
                        <div className="col-4">
                            <div className="d-flex flex-column">
                                <label
                                    htmlFor="attributeValue"
                                    className="input-label text-sm"
                                >
                                    Attribute Value:
                                    {(tooltipContent.attribute_value.flag || errorTooltipContent.attribute_value.flag) && (
                                        <LightTooltip
                                            title={tooltipContent.attribute_value.message || errorTooltipContent.attribute_value.message}
                                            offset={[20, -12]}
                                            placement="bottom"
                                        >
                                            <ErrorOutlineIcon
                                                style={{
                                                    fontWeight: "600",
                                                    color: "#FF0000",
                                                    fontSize: "16px",
                                                    marginLeft: "3px",
                                                }}
                                            />
                                        </LightTooltip>
                                    )}
                                    {cautionFlag.attribute_value && (
                                        <LightTooltip
                                            title={`A single open-ended backend variable can be mapped to only one 'Attribute Value' i.e. "${data.attribute_value}"`}
                                            offset={[20, -12]}
                                            placement="bottom"
                                        >
                                            <WarningAmberIcon
                                                style={{
                                                    fontWeight: "600",
                                                    color: "#FF0000",
                                                    fontSize: "16px",
                                                    marginLeft: "3px",
                                                }}
                                            />
                                        </LightTooltip>
                                    )}
                                </label>
                                {(productCategoryLabel === "Deodorants" && selectedStudyType.label === "Home Use Test") && (
                                    <span style={{ fontSize: "10px", color: "#6c757d", marginTop: "2px", lineHeight: "1.2" }}>
                                        (In case of open ended question, enter the open-ended question as asked to consumers)
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="col-8">
                            <input
                                id="attributeValue"
                                type="text"
                                value={data.attribute_value}
                                style={{
                                    color: ((type === 'add' && cautionFlag.attribute_value) ||  (type === 'update' && cautionFlag.attribute_value)) ? "rgb(153, 153, 153)" : "rgb(48, 48, 48)",
                                    backgroundColor: isOpenEndedVariable ? "rgb(242, 242, 242)" : "inherit",
                                }}
                                className="input-label-field text-sm"
                                onChange={(e) => handleInputChange(e.target.value, "text", "attribute_value")}
                                disabled={cautionFlag.attribute_value}
                                autoComplete="off"
                            />
                        </div>
                    </div>

                    <div className="row pb-2">
                        <div className="col-4">
                            <label
                                htmlFor="dataType"
                                className="input-label vertical-center text-sm"
                            >
                                Data Type:
                                {tooltipContent.data_type.flag ||
                                    (errorTooltipContent.data_type.flag && (
                                        <LightTooltip
                                            title={tooltipContent.data_type.message || errorTooltipContent.data_type.message}
                                            offset={[55, -12]}
                                            placement="bottom"
                                        >
                                            <ErrorOutlineIcon
                                                style={{
                                                    fontWeight: "600",
                                                    color: "#FF0000",
                                                    fontSize: "16px",
                                                    marginLeft: "3px",
                                                }}
                                            />
                                        </LightTooltip>
                                    ))}
                            </label>
                        </div>
                        <div className="col-8">
                            <Select
                                ref={selectInputRef}
                                className="text-sm input-label-select"
                                isClearable={true}
                                isSearchable={true}
                                options={dataTypeOptions}
                                value={selectedDataType}
                                onChange={(datum) => {
                                    handleInputChange(datum, "dropdown", "data_type");
                                    setSelectedDataType(datum);
                                }}
                                styles={{
                                    menuPortal: (base) => ({
                                        ...base,
                                        zIndex: 9999,
                                        fontSize: "0.875rem",
                                        lineHeight: "1.25rem",
                                    }),
                                }}
                                menuPortalTarget={document.body}
                                isDisabled={(type === 'add' ? cautionFlag.data_type : false) ||(type === "update" && dataView.value === 1 && (data.variable_category !== "Open Ended" || data.data_type === "Open Ended"))}                                
                            />
                        </div>
                    </div>

                    {/* Type Of Analysis conditional component */}
                    {showTypeOfAnalysis && (
                        <div className="row pb-2">
                            <div className="col-4">
                                <div className="d-flex flex-column">
                                    <label
                                        htmlFor="typeOfAnalysis"
                                        className="input-label text-sm d-flex align-items-center mb-0"
                                    >
                                        Type of Analysis:<span style={{ color: "red", marginLeft: "2px" }}>*</span>
                                        {(tooltipContent.type_of_analysis.flag || errorTooltipContent.type_of_analysis.flag) && (
                                            <LightTooltip
                                                title={tooltipContent.type_of_analysis.message || errorTooltipContent.type_of_analysis.message}
                                                offset={[40, -12]}
                                                placement="bottom"
                                            >
                                                <ErrorOutlineIcon
                                                    style={{
                                                        fontWeight: "600",
                                                        color: "#FF0000",
                                                        fontSize: "16px",
                                                        marginLeft: "3px",
                                                    }}
                                                />
                                            </LightTooltip>
                                        )}
                                    </label>
                                    <span style={{ fontSize: "10px", color: "red", marginTop: "2px", lineHeight: "1.2" }}>
                                        This field is mandatory
                                    </span>
                                </div>
                            </div>
                            <div className="col-8">
                                <Select
                                    className="text-sm input-label-select"
                                    isClearable={true}
                                    isSearchable={true}
                                    options={typeOfAnalysisOptions}
                                    value={selectedTypeOfAnalysis}
                                    onChange={(datum) => {
                                        handleInputChange(datum, "dropdown", "type_of_analysis");
                                        setSelectedTypeOfAnalysis(datum);
                                    }}
                                    styles={{
                                        menuPortal: (base) => ({
                                            ...base,
                                            zIndex: 9999,
                                            fontSize: "0.875rem",
                                            lineHeight: "1.25rem",
                                        }),
                                    }}
                                    menuPortalTarget={document.body}
                                    isDisabled={type === 'add' ? cautionFlag.type_of_analysis : false}
                                />
                            </div>
                        </div>
                    )}
                </DialogContent>
                <DialogActions
                    style={{
                        justifyContent: "center",
                        marginBottom: "20px",
                    }}
                >
                    <Button
                        className="custom-btn"
                        variant="outlined"
                        size="small"
                        onClick={handleReset}
                    >
                        Reset
                    </Button>
                    <Button
                        sx={{textTransform : 'None'}}
                        className={(!Object.values(errorTooltipContent).some((v) => v.flag)) && !(showTypeOfAnalysis &&(!data.type_of_analysis || data.type_of_analysis === "")) ? "custom-btn liner-light-blue-background" : ""}
                        size="small"
                        autoFocus
                        variant={showTypeOfAnalysis &&(!data.type_of_analysis || data.type_of_analysis === "")? "outlined": "contained"}
                        color="primary"
                        onClick={handleSubmit}
                        disabled={Object.values(errorTooltipContent).some((v) => v.flag) ||(showTypeOfAnalysis && (!data.type_of_analysis || data.type_of_analysis === ""))}
                    >
                        {type === "add" ? "Add" : "Update"}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

AddUpdateComponent.propTypes = {
    title: PropTypes.string,
    type: PropTypes.oneOf(["add", "update"]),
    selectedRow: PropTypes.object,
    fetchData: PropTypes.func,
    dataView: PropTypes.object,
    isOpen: PropTypes.bool,
    setIsAddDialogOpen: PropTypes.func,
    setIsUpdateDialogOpen: PropTypes.func,
    selectedStudyType: PropTypes.string,
    selectedStudyTypeMethod: PropTypes.string,
};

export default AddUpdateComponent;
